<template>
  <div v-if="loadedSchema" class="mt-5">
    <document-form
      :template-name="schemaName"
      :schema="$schema"
      :applicant="profile"
      :student="student"
      :school="school"
    />

    <attachments
      :attachments="stateData.attachments"
      :validated="validated"
      :show-radio="showRadio"
      :editable="true"
      :accept="fileLimits && fileLimits.accept"
      :max-size="fileLimits && fileLimits.maxSize"
      @errors="setAttachmentErrors"
    />

    <check-permission permissions="COMMENT.UPDATE">
      <comment :model="$schema.comment" />
    </check-permission>

    <panel v-if="failedCriterions.length" title="Komunikat">
      <div class="row form-group">
        <div class="col-12">
          <div class="alert alert-danger mb-0" role="alert">
            <span>
              Wniosek o którym mowa powyżej wymaga uzupełnienia w zakresie{{
                failedCriterions.length === 1 ? ' ' : ':'
              }}

              <span v-if="failedCriterions.length === 1">{{ failedCriterions[0].formula }}.</span>

              <ul v-else>
                <li v-for="criterion in failedCriterions" :key="criterion.id">{{ criterion.formula }}</li>
              </ul>
            </span>
          </div>
        </div>
      </div>
    </panel>

    <div class="row mt-5">
      <div class="col-md-12">
        <w-warnings v-if="validated" title="UWAGA:" :model="$schema" :display-errors="true" />

        <div v-if="validated && attachmentsErrors.length">
          <p v-if="!$schema.$checkAll().length && !$schema.$validateAll().length" style="font-weight: bold;">UWAGA:</p>

          <ul class="warnings-list">
            <li
              v-for="(attachmentError, index) in attachmentsErrors"
              :key="'attachmentsErrors_' + index"
              class="warning"
            >
              {{ attachmentError.$title || attachmentError.$key }}: {{ attachmentError.$warnings[0] }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <fields-required />

    <div class="row mt-5">
      <div class="col-md-12">
        <button class="btn btn-secondary float-left" @click="cancel">Anuluj</button>

        <button class="btn btn-primary float-right" @click="submit">Zapisz</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { EntityType } from '@/enums/entityType.enum';
import { Errors } from '@/enums/errors.enum';
import { Options } from '@/enums/options.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { editMixin } from '@/mixins/edit';
import { formRenderContainerMixin } from '@/mixins/formRenderContainer';
import { validationMessages } from '@/schemas/validationMessages.schema';
import { documentWebsocket } from '@/services/documentWebsocket.service';
import { AUTOSAVE, VALIDATE_DATA_FUNCTIONS } from '@/utils/constants';
import { flat } from '@/utils/flat';
import { tryParseInt } from '@/utils/number';
import { getProfileId, loadPreviousApplicantPermission } from '@/utils/profile';

// @vuese
// Szczegółowe dane dokumentu
export default {
  components: {
    FieldsRequired: () =>
      import(/* webpackChunkName: "FieldsRequired" */ '@/components/formComponents/FieldsRequired.vue'),
    DocumentForm: () => import(/* webpackChunkName: "DocumentForm" */ '@/components/forms/DocumentForm.vue'),
    Attachments: () => import(/* webpackChunkName: "Attachments" */ '@/components/document/Attachments.vue'),
    Comment: () => import(/* webpackChunkName: "Comment" */ '@/components/formComponents/Comment.vue'),
    Panel: () => import(/* webpackChunkName: "Panel" */ '@/components/panel/Panel.vue')
  },
  mixins: [{ ...formRenderContainerMixin, mounted: undefined }, editMixin('document')],
  data() {
    return {
      timeoutId: undefined,
      validated: false,
      attachmentsErrors: []
    };
  },
  computed: {
    ...mapState({
      stateData(state) {
        return {
          ...state.document.data,
          comment: '',
          data: state.document.data.data || {},
          childrenModels: state.document.data.children,
          attachments: state.document.attachment.list.filter(function(attachment) {
            return (
              attachment.entityId === this.$route.params.documentId.toString() &&
              attachment.entityType === EntityType.DOCUMENT
            );
          }, this)
        };
      },
      termPassed: state => state.optionValue.options[Options.TERM_PASSED].value,
      schema: state => {
        const schema = state.schema.data && JSON.parse(state.schema.data.schema);
        schema.properties.comment = {
          title: 'Treść',
          type: 'string',
          fieldRequired: false
        };
        schema.properties.creatorId = { type: 'number' };
        schema.$options = schema.$options || {};
        schema.$options.validationMessages = {
          ...validationMessages.$options.validationMessages,
          ...schema.$options.validationMessages
        };
        schema.$options.warningMessages = {
          ...validationMessages.$options.warningMessages,
          ...schema.$options.warningMessages
        };
        return schema;
      },
      settings: state => state.setting.settings,
      showRadio: state => state.setting.settings[PublicSettings.ATTACHMENTS_SHOW_RADIO]?.value === 'true',
      criterions: state => state.criterion.list.filter(criterion => !!criterion.formula),
      profile: state => {
        if (!state.auth.checkPermission.permissions[loadPreviousApplicantPermission()]) {
          return {};
        }
        return state.auth.data.data?.applicantData
          ? { ...state.auth.data, ...state.auth.data.data.applicantData }
          : state.auth.data;
      },
      student: state => (state.person.list?.length === 1 ? state.person.list[0] : undefined),
      school(state) {
        if (!this.student?.structureUid) {
          return;
        }
        return state.structureNode.externalMap[this.student.structureUid];
      },
      childProcedureId: state => tryParseInt(state.setting.settings[PublicSettings.CHILD_PROCEDURE_ID]?.value),
      fileLimits: state =>
        state.setting.settings[PublicSettings.FILE_LIMITS]?.value &&
        JSON.parse(state.setting.settings[PublicSettings.FILE_LIMITS].value)
    }),
    failedCriterions() {
      return this.criterions
        ? this.criterions.filter(
            criterion =>
              new Function('schema', 'document', criterion.autoFunction)(this.$schema, this.$schema.$getData()) === 3
          )
        : [];
    }
  },
  watch: {
    model: {
      deep: true,
      handler() {
        if (!this.timeoutId) {
          this.timeoutId = setTimeout(() => this.autosave(), 60000);
        }
      }
    }
  },
  mounted() {
    this.$options.external = {
      termPassed: this.termPassed === 'true',
      settings: this.settings,
      childProcedureId: this.childProcedureId
    };
    if (this.stateData?.data?.tenderer?.pesel) {
      this.stateData.data.tenderer.peselInput = this.stateData.data.tenderer.pesel;
    }
    this.loadSchema();
    documentWebsocket.onForceUnlock(() =>
      this.autosave().then(() => this.$route.push({ name: 'documentView', params: this.$route.params }))
    );
  },
  beforeDestroy() {
    clearTimeout(this.timeoutId);
  },
  methods: {
    // @vuese
    // Anuluje dodawanie/edycję
    cancel() {
      this.$router.push({ name: 'documentView', query: this.$route.query });
    },
    // @vuese
    // Dodaje ostrzeżenia do modelu
    prepareModel(model, saveWarnings = true) {
      if (saveWarnings) {
        model.data.warnings = flat(this.$schema.$checkAll().map(item => item.$warnings));
      }
      return model;
    },
    // @vuese
    // Funkcja wykonywana po zapisie
    afterSave() {
      const model = this.$schema.$getData();
      const creatorId = this.$store.state.document.data.creatorId;
      const profileId = getProfileId();
      if (model?.data?.applicant && creatorId === profileId) {
        this.$store
          .dispatch('profile/setApplicantData', {
            profileId,
            applicantData: model.data.applicant
          })
          .then(() => this.$store.dispatch('auth/loadData', { force: true }));
      }

      this.$router.push({ name: 'documentView', query: this.$route.query });
    },
    // @vuese
    // Autozapis danych
    autosave() {
      const model = { ...this.$schema.$getData(), id: this.id };
      return this.$store
        .dispatch('document/update', { ...this.prepareModel(model, false), preventLoader: true, comment: AUTOSAVE })
        .then(() => this.notifySuccess(Successes.DOCUMENT_AUTOSAVED))
        .then(() => {
          this.timeoutId = undefined;
          if (this.$schema.childrenModels) {
            return this.$store
              .dispatch('document/get', { id: this.id, preventLoader: true })
              .then(document => this.$schema.childrenModels.$setData(document.children))
              .catch(err => this.notifyError(Errors.DOCUMENT_CANNOT_GET));
          }
        })
        .catch(err => this.notifyError(Errors.FORM_CANNOT_SAVE));
    },
    // @vuese
    // Przetwarza formularz
    submit() {
      this.validate().then(errors => {
        const schemaErrors = this.$schema
          .$validateAll()
          .filter(
            error =>
              !VALIDATE_DATA_FUNCTIONS.some(validateDataFunction => validateDataFunction.schemaField === error.$key)
          )
          .map(error => `${this.fieldTitle(error)} ${error.$error}`);

        const schemaWarnigns = this.$schema
          .$checkAll()
          .filter(
            warning =>
              !VALIDATE_DATA_FUNCTIONS.some(validateDataFunction => validateDataFunction.schemaField === warning.$key)
          )
          .map(warning => `${this.fieldTitle(warning)} ${warning.$warnings[0]}`);

        const attachmentWarnings = this.attachmentsErrors.map(
          warning => `${this.fieldTitle(warning, false)} ${warning.$warnings[0]}`
        );

        errors.push(...schemaErrors, ...schemaWarnigns, ...attachmentWarnings);
        const importantError = this.$schema.data.veryImportantErrors?.$model
          ? `<p class="text-red font-larger font-weight-bold">${this.$schema.data.veryImportantErrors.$model}</p>`
          : '';

        if (errors.length) {
          return this.showSaveDialog(errors, importantError);
        }

        this.save();
      });
    },

    // @vuese
    // Zwraca nazwę pola wraz z :
    fieldTitle(field, short = true) {
      const title = (short ? field.$shortTitle : field.$title) || field.$key;
      return title.endsWith(':') ? title : `${title}:`;
    },

    // @vuese
    // Waliduje formularz
    async validate() {
      const errors = [];

      for (const validateDataFunction of VALIDATE_DATA_FUNCTIONS) {
        const validationResult = await this.validateData(
          validateDataFunction.functionName,
          validateDataFunction.schemaField
        );

        if (validationResult) {
          this.$schema.$validateAll();
          this.$schema.$checkAll();
          errors.push(validationResult);
        }
      }

      if (this.$schema.$validateAll().length !== 0 || this.$schema.$checkAll().length !== 0) {
        errors.push(Errors.FORM_INCORRECTLY_COMPLETED);
      }

      this.validated = true;
      return errors;
    },

    // @vuese
    // Waliduje dane dokumentu
    validateData(functionName, schemaField) {
      const field = this.$schema.data[schemaField];
      return this.$store
        .dispatch(`document/${functionName}`, {
          id: tryParseInt(this.$route.params.documentId),
          model: {
            ...this.$schema.$getData(),
            procedureId: tryParseInt(this.$route.params.procedureId)
          }
        })
        .then(validationResult => {
          field.$setData(validationResult);
          return field.$error || field.$warnings?.[0];
        });
    },
    // @vuese
    // Otwiera okienko z potwierdzeniem zapisu formularza
    showSaveDialog(errors, beforeDescription) {
      const totalErrors = errors.length;
      if (totalErrors > 5) {
        errors = errors.slice(0, 5);
      }
      const description =
        beforeDescription +
        errors.join('<br />') +
        (totalErrors > 5 ? '<br /><small>i ' + (totalErrors - 5) + ' więcej...</small>' : '') +
        '<br />Czy chcesz zapisać go w obecnej formie?';
      this.$store.dispatch('setConfirm', {
        description,
        title: 'Błędy',
        closeButton: 'Nie - powróć do edycji formularza',
        submitButton: 'Tak - wrócę do edycji później',
        onSuccess: () => this.save()
      });
    },
    // @vuese
    // ustawienie błędów schemy załączników
    setAttachmentErrors(errors) {
      this.attachmentsErrors = errors.map(error => ({
        key: error.key,
        $title: error.$title,
        $warnings: error.$warnings
      }));
    }
  },
  beforeRouteLeave: (to, from, next) => {
    documentWebsocket.unlockDocumentEdit();
    next();
  }
};
</script>

<style>
ul.warnings-list {
  margin-bottom: 0;
}
</style>
